<template>
  <div
    class="col p-0"
    data-cy="handover-devices-step"
    :data-view-ready="!loading">
    <div class="text-center text-danger my-2">
      <coozzy-spinner
        v-if="loading" />
    </div>
    <div
      class="row">
      <b-modal
        v-if="deviceToEdit"
        :id="'editDevice'"
        ref="editDevice"
        :key="internalKey"
        data-cy="handover-devices-edit-modal"
        :title="$t('message.onBoarding.buildings.objects.renovation.editDevice.title')"
        no-close-on-backdrop
        hide-footer
        @hide="handleDeviceModalClose">
        <div class="col">
          <device
            ref="object-device"
            :target="'object'"
            :from-handover="true"
            :only-devices="!deviceToEdit.roomId"
            :handover-rooms="preSelectedRooms"
            :device="deviceToEdit"
            :expand-by-default="true"
            :owner-id="object?.ownerId || accountId"
            @device-update="deviceUpdated" />
        </div>
        <div class="col mt-2">
          <coozzy-button
            data-cy="handover-devices-edit-cancel-button"
            size="small"
            class="float-left mb-0 border"
            design="transparent"
            @click="hideEditDeviceModal">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            data-cy="handover-devices-edit-save-button"
            design="green"
            class="float-right ml-3"
            @click="saveDevice()">
            {{ $t('message.generic.form.save') }}
          </coozzy-button>
        </div>
      </b-modal>
      <coozzy-confirmation-modal
        ref="device-confirmation-modal"
        @save="saveDeviceAndClose"
        @discard="discardDeviceChanges"
        @cancel="cancelDeviceModalClose" />
      <b-modal
        v-if="selectedRoomModal"
        :id="'addDevice'"
        ref="addDevice"
        data-cy="handover-devices-add-modal"
        no-close-on-backdrop
        :title="selectedRoomModal.name"
        hide-footer
        @hide="handleAddDeviceModalClose">
        <div class="row mx-1">
          <div class="col-12">
            <div class="row">
              <div
                class="col-3 offset-9">
                <label for="room">{{ $t('message.onBoarding.buildings.objects.type.title') }}</label>
                <coozzy-form-select
                  id="room"
                  v-model="roomAvailable"
                  data-cy="handover-devices-add-room-select"
                  class="select-entries float-right"
                  name="room">
                  <option value="">
                    -
                  </option>
                  <option
                    v-for="(room, indexOption) in availableRooms"
                    :key="indexOption"
                    :value="room">
                    {{ room.value }}
                  </option>
                </coozzy-form-select>
              </div>
            </div>
          </div>
          <div
            v-if="selectedRoomModal"
            class="col-md-5">
            <h3 data-cy="handover-devices-add-existing-title">
              {{ source === 'template' ? $t('message.processHandoverTemplate.addDevices') : $t('message.processHandover.rooms.existingDevices') }}
              <coozzy-info-circle-icon
                v-if="source !== 'template'"
                v-b-tooltip.hover.html="$t('message.processHandover.rooms.existingDevicesTooltip')"
                class="ml-2 mb-2 tooltip-message" />
            </h3>
            <p v-if="source !== 'template'">
              {{ $t('message.processHandover.rooms.existingDevicesWithinRoom') }}
            </p>
            <div class="device-list">
              <draggable
                v-model="selectedRoomModal.devices"
                @end="updateModalDevicesOrder">
                <div
                  v-for="(device, index) in selectedRoomModal.devices"
                  :key="(device.id || device.internalId) + '-' + device.type + '-' + index"
                  :class="['list-group-item device-list']"
                  data-cy="handover-devices-add-existing-item">
                  <span data-cy="handover-devices-add-existing-item-name">{{ device.value }}</span>
                  <coozzy-button
                    data-cy="handover-devices-add-existing-item-delete-button"
                    design="transparent"
                    class="p-0 ml-1 mb-2 float-right"
                    @click="showDeleteConfirmation(device, $event)">
                    <coozzy-delete-icon />
                  </coozzy-button>
                  <coozzy-button
                    data-cy="handover-devices-add-existing-item-edit-button"
                    design="transparent"
                    class="p-0 mb-2 float-right ml-2"
                    @click="editDevice(device, selectedRoomModal)">
                    <coozzy-edit-icon />
                  </coozzy-button>
                </div>
              </draggable>
            </div>
            <coozzy-button
              data-cy="handover-devices-add-new-device-button"
              class="col-sm-12 col-lg-4 col-md-4 float-md-right mt-3"
              design="prop-green"
              @click="addDevice($event)">
              {{ $t('message.onBoarding.devices.addDevices') }}
            </coozzy-button>
          </div>
          <div class="col-md-5 offset-2">
            <h3 data-cy="handover-devices-add-suggestions-title">
              {{ $t('message.processHandover.rooms.suggestionsDevices') }}
              <coozzy-info-circle-icon
                v-if="suggestionDevices.length > 0 && templateObject"
                v-b-tooltip.hover.html="$t('message.processHandover.rooms.suggestionsDevicesTooltip')"
                class="ml-2 mb-2 tooltip-message" />
            </h3>
            <p
              v-if="suggestionDevices.length > 0 && templateObject"
              class="m-0"
              data-cy="handover-devices-add-template-name">
              {{ $t('message.settings.template.template') }}: {{ templateObject.name }}
            </p>
            <p v-else>
              {{ $t('message.processHandover.rooms.suggestionsDevicesDescription') }}
            </p>
            <coozzy-button
              data-cy="handover-devices-add-all-button"
              class="float-right modal-add-all"
              design="prop-green"
              :disabled="!selectedRoomModal"
              @click="addAllDevices(roomAvailable)">
              {{ $t('message.onBoarding.devices.addAll') }}
            </coozzy-button>
            <div
              v-if="roomAvailable"
              class="device-list">
              <div
                v-for="device in roomAvailable.devices"
                :key="device.id || device.internalId"
                :class="['list-group-item', 'with-add-button']"
                data-cy="handover-devices-add-suggestion-item">
                <span data-cy="handover-devices-add-suggestion-item-name">{{ device.value }}</span>
                <coozzy-button
                  data-cy="handover-devices-add-suggestion-item-add-button"
                  design="prop-green"
                  class="p-0 ml-1 float-right px-2 add-to-available"
                  @click="addDeviceMethod(device)">
                  +
                </coozzy-button>
              </div>
            </div>
          </div>
        </div>
        <div class="col mt-2">
          <coozzy-button
            data-cy="handover-devices-add-cancel-button"
            size="small"
            class="float-left mb-0 border"
            design="transparent"
            @click="$bvModal.hide('addDevice')">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            data-cy="handover-devices-add-finish-button"
            design="green"
            class="float-right ml-3"
            @click="saveEditDeviceOfRoom()">
            {{ $t('message.processHandover.devices.finish') }}
          </coozzy-button>
        </div>
      </b-modal>
      <coozzy-confirmation-modal
        ref="add-device-confirmation-modal"
        @save="saveAddDeviceAndClose"
        @discard="discardAddDeviceChanges"
        @cancel="cancelAddDeviceModalClose" />
      <div
        v-for="(room, index) in filteredSelectedRooms"
        :id="room.id"
        :key="room.id"
        class="room-container col-md-5 mr-auto"
        data-cy="handover-devices-room-card"
        @click="selectSelectedRoom(room)">
        <coozzy-card
          class="list-group">
          <div
            v-b-toggle="'collapse-customer-' + index"
            :class="'collapse-title-' + index">
            <span
              class="width-only-content"
              data-cy="handover-devices-room-title">
              {{ room.name }}
            </span>
            <coozzy-button
              data-cy="handover-devices-add-devices-button"
              class="float-right center-add-all"
              design="default"
              @click="openAddDeviceModal(room, $event)">
              {{ $t('message.onBoarding.devices.addDevices') }}
            </coozzy-button>
          </div>
          <div
            class="device-list mt-2">
            <b-collapse
              :id="'collapse-customer-' + index"
              visible>
              <draggable
                v-model="room.devices"
                @end="updateDeviceOrder(room)">
                <div
                  v-for="(device, deviceIndex) in room.devices"
                  :key="(device.id || device.internalId) + '-' + device.type + '-' + deviceIndex"
                  :class="['list-group-item']"
                  data-cy="handover-devices-device-item">
                  <span
                    :class="['condition-dot', device.condition]"
                    data-cy="handover-devices-device-condition" />
                  <span data-cy="handover-devices-device-name">{{ device.value }}</span>
                  <div class="float-right d-flex align-items-center">
                    <coozzy-button
                      v-if="device.note"
                      v-b-tooltip.hover.html="device.note"
                      design="transparent"
                      class="p-0 mb-1 ml-2"
                      @click="editDevice(device, room)">
                      <coozzy-comment-icon-img />
                    </coozzy-button>
                    <coozzy-button
                      data-cy="handover-devices-device-edit-button"
                      design="transparent"
                      class="p-0 mb-1 ml-2"
                      @click="editDevice(device, room)">
                      <coozzy-edit-icon />
                    </coozzy-button>
                    <coozzy-button
                      data-cy="handover-devices-device-delete-button"
                      design="transparent"
                      class="p-0 mb-1 ml-1"
                      @click="showDeleteConfirmation(device, $event, room)">
                      <coozzy-delete-icon />
                    </coozzy-button>
                  </div>
                </div>
              </draggable>
            </b-collapse>
          </div>
        </coozzy-card>
      </div>
      <div
        class="room-container col-md-5 mr-auto"
        data-cy="handover-devices-unassigned-card">
        <coozzy-card
          class="list-group">
          <div
            v-b-toggle="'collapse-devicesWithoutRoom'">
            <span class="width-only-content">{{ $t('message.processHandover.devices.devicesWithoutRoom') }}</span>
            <coozzy-button
              data-cy="handover-devices-add-devices-button"
              class="float-right center-add-all"
              design="default"
              @click="addDevice($event)">
              {{ $t('message.onBoarding.devices.addDevices') }}
            </coozzy-button>
          </div>
          <div
            class="device-list mt-2">
            <b-collapse
              :id="'collapse-devicesWithoutRoom'"
              visible>
              <draggable
                v-model="filteredDevicesWithoutRooms"
                @end="updateDevicesWithoutRoomsOrder">
                <div
                  v-for="device in filteredDevicesWithoutRooms"
                  :key="(device.id || device.internalId) + '-' + device.type"
                  :class="['list-group-item']"
                  data-cy="handover-devices-device-item">
                  <span
                    :class="['condition-dot', device.condition]"
                    data-cy="handover-devices-device-condition" />
                  <span data-cy="handover-devices-device-name">{{ device.value }}</span>
                  <div class="float-right d-flex align-items-center">
                    <coozzy-button
                      v-if="device.note"
                      v-b-tooltip.hover.html="device.note"
                      design="transparent"
                      class="p-0 mb-1 ml-2"
                      @click="editDevice(device, null)">
                      <coozzy-comment-icon-img />
                    </coozzy-button>
                    <coozzy-button
                      data-cy="handover-devices-device-edit-button"
                      design="transparent"
                      class="p-0 mb-1 ml-2"
                      @click="editDevice(device, null)">
                      <coozzy-edit-icon />
                    </coozzy-button>
                    <coozzy-button
                      data-cy="handover-devices-device-delete-button"
                      design="transparent"
                      class="p-0 mb-1 ml-1"
                      @click="showDeleteConfirmation(device, $event)">
                      <coozzy-delete-icon />
                    </coozzy-button>
                  </div>
                </div>
              </draggable>
              <div
                v-if="hasSuggestions"
                class="mt-2">
                <p class="mb-1 font-weight-bold suggestions-label">
                  {{ $t('message.processHandover.devices.suggestions') }}
                </p>
                <div
                  v-if="showLetterboxSuggestion"
                  class="list-group-item with-add-button"
                  data-cy="handover-devices-add-suggestion-item">
                  <span data-cy="handover-devices-add-suggestion-item-name">{{ $t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_LETTER_BOX') }}</span>
                  <coozzy-button
                    data-cy="handover-devices-add-suggestion-item-add-button"
                    design="prop-green"
                    class="p-0 ml-1 float-right px-2 add-to-available"
                    @click="addSuggestedLetterbox()">
                    +
                  </coozzy-button>
                </div>
                <div
                  v-for="device in templateUnassignedSuggestions"
                  :key="device.id"
                  class="list-group-item with-add-button"
                  data-cy="handover-devices-add-suggestion-item">
                  <span data-cy="handover-devices-add-suggestion-item-name">{{ device.value }}</span>
                  <coozzy-button
                    data-cy="handover-devices-add-suggestion-item-add-button"
                    design="prop-green"
                    class="p-0 ml-1 float-right px-2 add-to-available"
                    @click="addSuggestedTemplateDevice(device)">
                    +
                  </coozzy-button>
                </div>
              </div>
            </b-collapse>
          </div>
        </coozzy-card>
      </div>
    </div>
    <b-modal
      :id="'confirmationModalDelete_' + objectId"
      :ref="'confirmationModalDelete_' + objectId"
      data-cy="handover-devices-delete-confirm-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.processHandover.confirmationDeletion.title')">
      <template>
        <div class="col p-0">
          <p>{{ $t('message.processHandover.confirmationDeletion.body') }}</p>
        </div>
        <div class="col p-0">
          <coozzy-button
            data-cy="handover-devices-delete-cancel-button"
            size="small"
            class="mb-0 border"
            design="transparent"
            @click="$bvModal.hide('confirmationModalDelete_' + objectId)">
            {{ $t('message.generic.cancel') }}
          </coozzy-button>
          <coozzy-button
            data-cy="handover-devices-delete-confirm-button"
            size="small"
            class="float-right mb-0"
            design="green"
            @click="selectedRoomModal ? deleteDeviceModal() : deleteDevice()">
            {{ $t('message.generic.delete') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
    <b-modal
      :id="'duplicateConfirmationModal'"
      :ref="'duplicateConfirmationModal'"
      data-cy="handover-devices-duplicate-confirm-modal"
      no-close-on-backdrop
      hide-footer
      :title="$t('message.processHandover.duplicateDevices.title')">
      <template>
        <div class="col p-0">
          <p>{{ $t('message.processHandover.duplicateDevices.message') }}</p>
          <ul
            class="mt-3"
            data-cy="handover-devices-duplicate-list">
            <li
              v-for="device in duplicateDevices"
              :key="device.internalId"
              data-cy="handover-devices-duplicate-list-item">
              {{ device.value }}
            </li>
          </ul>
        </div>
        <div class="col p-0 mt-3">
          <coozzy-button
            data-cy="handover-devices-duplicate-no-button"
            size="small"
            class="mb-0 border"
            design="transparent"
            @click="handleDuplicateConfirmation(false)">
            {{ $t('message.generic.selectField.no') }}
          </coozzy-button>
          <coozzy-button
            data-cy="handover-devices-duplicate-yes-button"
            size="small"
            class="float-right mb-0"
            design="green"
            @click="handleDuplicateConfirmation(true)">
            {{ $t('message.generic.selectField.yes') }}
          </coozzy-button>
        </div>
      </template>
    </b-modal>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import draggable from 'vuedraggable'
import { formatting } from '@/mixins/formatting'
import { deviceUtils } from '@/mixins/deviceUtils'
import { obj } from '@/mixins/object'
import { onboarding } from '@/mixins/onboarding'
import { routeChecks } from '@/mixins/routeChecks'
import { algolia } from '@/mixins/algolia'
import CoozzySpinner from '@/framework/components/misc/CoozzySpinner.vue'
import { mapActions, mapGetters } from 'vuex'
import CoozzyEditIcon from '@/framework/components/icons/CoozzyEditIcon.vue'
import CoozzyButton from '@/framework/components/button/CoozzyButton.vue'
import Device from '@/building/tabs/components/Device.vue'
import CoozzyDeleteIcon from '@/framework/components/icons/CoozzyDeleteIcon.vue'
import CoozzyCard from '@/framework/components/card/CoozzyCard.vue'
import { helper } from '@/types/helper'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import CoozzyInfoCircleIcon from '@/framework/components/icons/CoozzyInfoCircleIcon.vue'
import CoozzyCommentIconImg from '@/framework/components/icons/CoozzyCommentIconImg.vue'
import CoozzyConfirmationModal from '@/framework/components/modal/CoozzyConfirmationModal.vue'
import {
  ObjectobjectObject,
  ObjecthandoverProtocolTemplate,
  ObjectroomRoom
} from '@/types/api'
import { HandoverDevice, HandoverRoom } from '@/types/models'

interface AvailableRoom {
  name: string
  value: string
  devices: HandoverDevice[]
}

export default Vue.extend({
  name: 'HandoverDevicesStep',
  components: {
    CoozzyCommentIconImg,
    CoozzyInfoCircleIcon,
    CoozzyFormSelect,
    CoozzyCard,
    CoozzyDeleteIcon,
    Device,
    CoozzyButton,
    CoozzyEditIcon,
    CoozzySpinner,
    draggable,
    CoozzyConfirmationModal
  },
  mixins: [formatting, obj, onboarding, routeChecks, algolia, deviceUtils],
  props: {
    initKey: {
      type: Number,
      default: 0
    },
    object: {
      type: Object as PropType<ObjectobjectObject | null>,
      default: () => null
    },
    preSelectedRooms: {
      type: Array as PropType<HandoverRoom[]>,
      default: () => []
    },
    devices: {
      type: Array as PropType<HandoverDevice[]>,
      default: () => []
    },
    handoverDeletedDeviceIds: {
      type: Array as PropType<string[]>,
      default: () => []
    },
    resetDevicesWithoutRooms: {
      type: Boolean,
      default: false
    },
    source: {
      type: String,
      required: false,
      default: () => ''
    },
    templateObject: {
      type: Object as PropType<ObjecthandoverProtocolTemplate | null>,
      required: false,
      default: () => null
    },
    suggestionRooms: {
      type: Array as PropType<ObjectroomRoom[]>,
      default: () => []
    },
    suggestionDevices: {
      type: Array as PropType<HandoverDevice[]>,
      default: () => []
    }
  },
  data() {
    return {
      deviceToEditInit: null as HandoverDevice | null,
      roomAvailable: null as AvailableRoom | null,
      loading: true as boolean,
      internalKey: 0 as number,
      availableRooms: [] as AvailableRoom[],
      selectedRooms: [] as HandoverRoom[],
      devicesStepDeletedDeviceIds: [] as string[],
      isInternalDeviceSync: false as boolean,
      selectedEntries: [] as HandoverDevice[],
      filteredObjects: [] as ObjectobjectObject[],
      collapsedRooms: [] as boolean[],
      selectedDevice: null as HandoverDevice | null,
      usedContacts: [] as Record<string, string>[],
      deviceToEdit: null as HandoverDevice | null,
      selectedRoom: null as HandoverRoom | null,
      selectedRoomModal: null as HandoverRoom | null,
      selectedEditedDevice: null as HandoverDevice | null,
      devicesWithoutRooms: [] as HandoverDevice[],
      pendingNewDevice: null as HandoverDevice | null,
      mappingKeys: [
        { word: 'Küche', mapping: 'kitchen' },
        { word: 'kitchen', mapping: 'kitchen' },
        { word: 'cuisine', mapping: 'kitchen' },
        { word: 'Bad', mapping: 'wetRoom' },
        { word: 'Badezimmer', mapping: 'wetRoom' },
        { word: 'Nasszelle', mapping: 'wetRoom' },
        { word: 'Dusche', mapping: 'wetRoom' },
        { word: 'WC', mapping: 'wetRoom' },
        { word: 'Wohnzimmer', mapping: 'room' },
        { word: 'Stube', mapping: 'room' },
        { word: 'Innenraum', mapping: 'room' },
        { word: 'Zimmer', mapping: 'room' },
        { word: 'Schlafzimmer', mapping: 'room' },
        { word: 'Kinderzimmer', mapping: 'room' },
        { word: 'Umkleidezimmer', mapping: 'room' },
        { word: 'Spielzimmer', mapping: 'room' },
        { word: 'Raumlichkeit', mapping: 'room' },
        { word: 'Korridor', mapping: 'corridor_entrance' },
        { word: 'Gang', mapping: 'corridor_entrance' },
        { word: 'Durchgang', mapping: 'corridor_entrance' },
        { word: 'Flur', mapping: 'corridor_entrance' },
        { word: 'Eingangsbereich', mapping: 'corridor_entrance' },
        { word: 'Eingang', mapping: 'corridor_entrance' },
        { word: 'Balkon', mapping: 'balcony_seatingArea' },
        { word: 'Sitzplatz', mapping: 'balcony_seatingArea' },
        { word: 'Terrasse', mapping: 'balcony_seatingArea' },
        { word: 'Veranda', mapping: 'balcony_seatingArea' },
        { word: 'Loggia', mapping: 'balcony_seatingArea' },
        { word: 'Keller', mapping: 'cellar' },
        { word: 'Tiefgeschoss', mapping: 'cellar' },
        { word: 'Souterrain', mapping: 'cellar' },
        { word: 'Abstellraum', mapping: 'reduit_storage' },
        { word: 'Besenkammer', mapping: 'reduit_storage' },
        { word: 'Putzkammer', mapping: 'reduit_storage' },
        { word: 'Putzraum', mapping: 'reduit_storage' },
        { word: 'Stauraum', mapping: 'reduit_storage' },
        { word: 'Abstellkammer', mapping: 'reduit_storage' },
        { word: 'Cucina', mapping: 'kitchen' },
        { word: 'Bagno', mapping: 'wetRoom' },
        { word: 'Camera umida', mapping: 'wetRoom' },
        { word: 'Doccia', mapping: 'wetRoom' },
        { word: 'Soggiorno', mapping: 'room' },
        { word: 'Salotto', mapping: 'room' },
        { word: 'Interno', mapping: 'room' },
        { word: 'La camera', mapping: 'room' },
        { word: 'Camera da letto', mapping: 'room' },
        { word: 'Camera dei bambini', mapping: 'room' },
        { word: 'Spogliatoio', mapping: 'room' },
        { word: 'Sala giochi', mapping: 'room' },
        { word: 'Spazio', mapping: 'room' },
        { word: 'Corridoio', mapping: 'corridor_entrance' },
        { word: 'Ingranaggio', mapping: 'corridor_entrance' },
        { word: 'Passaggio', mapping: 'corridor_entrance' },
        { word: "Area d'ingresso", mapping: 'corridor_entrance' },
        { word: 'Ingresso', mapping: 'corridor_entrance' },
        { word: 'Balcone', mapping: 'balcony_seatingArea' },
        { word: 'Sedile', mapping: 'balcony_seatingArea' },
        { word: 'Terrazza', mapping: 'balcony_seatingArea' },
        { word: 'Cantina', mapping: 'cellar' },
        { word: 'Piano interrato', mapping: 'cellar' },
        { word: 'Seminterrato', mapping: 'cellar' },
        { word: 'Magazzino', mapping: 'reduit_storage' },
        { word: 'Armadio per le scope', mapping: 'reduit_storage' },
        { word: 'Stanza delle pulizie', mapping: 'reduit_storage' },
        { word: 'Locale per le pulizie', mapping: 'reduit_storage' },
        { word: 'Ripostiglio', mapping: 'reduit_storage' },
        { word: 'Magazzino', mapping: 'reduit_storage' },
        { word: 'Parking', mapping: 'parking' },
        { word: 'Parkplatz', mapping: 'parking' },
        { word: 'Posteggio', mapping: 'parking' },
        { word: 'Secondary room', mapping: 'secondaryRoom' },
        { word: 'Nebenraum', mapping: 'secondaryRoom' },
        { word: 'Camera secondarie', mapping: 'secondaryRoom' }
      ],
      duplicateDevices: [] as HandoverDevice[],
      pendingDevices: [] as HandoverDevice[],
      addDeviceToEditInit: null as HandoverRoom | null
    }
  },
  computed: {
    accountId(): string | null {
      return this.$store.getters['user/getAccountId'] || null
    },
    showLetterboxSuggestion(): boolean {
      if (!this.object) return false
      return ['APARTMENT', 'AGRICULTURE', 'COMMERCIAL', 'GARDEN', 'HOUSE'].includes(this.object.category)
    },
    templateUnassignedSuggestions(): HandoverDevice[] {
      return this.suggestionDevices
        .filter((d: any) => !d.roomId &&
          d.type !== 'DEVICE_TYPE_LOCKING_KEY' &&
          d.type !== 'DEVICE_TYPE_LOCKING_WIRELESS_TRANSMITTER'
        )
        .map((d: any) => ({
          ...d,
          value: this.$t('message.onBoarding.devices.typeValues.' + d.type)
        }))
    },
    hasSuggestions(): boolean {
      return this.showLetterboxSuggestion || this.templateUnassignedSuggestions.length > 0
    },
    objectId(): string {
      return this.object?.id || 'objectId'
    },
    filteredDevicesWithoutRooms: {
      get(): HandoverDevice[] {
        return this.devicesWithoutRooms.filter(d =>
          !this.isDeviceUninstalledInPast(d) &&
          d.type !== 'DEVICE_TYPE_LOCKING_KEY' &&
          d.type !== 'DEVICE_TYPE_LOCKING_WIRELESS_TRANSMITTER'
        )
      },
      set(devices: HandoverDevice[]) {
        this.devicesWithoutRooms = [
          ...devices,
          ...this.devicesWithoutRooms.filter(d =>
            this.isDeviceUninstalledInPast(d) ||
            d.type === 'DEVICE_TYPE_LOCKING_KEY' ||
            d.type === 'DEVICE_TYPE_LOCKING_WIRELESS_TRANSMITTER'
          )
        ]
      }
    },
    filteredSelectedRooms(): HandoverRoom[] {
      return this.selectedRooms.map(room => ({
        ...room,
        devices: room.devices.filter(d => !this.isDeviceUninstalledInPast(d))
      }))
    }
  },
  watch: {
    devicesStepDeletedDeviceIds(newVal: string[]) {
      this.$emit('deleted-devices', newVal, this.object || null)
    },
    selectedRooms(newVal: HandoverRoom[]) {
      if (this.isInternalDeviceSync) {
        return
      }
      this.$emit('selected-rooms', newVal, this.object || null, 'device')
    },
    devicesWithoutRooms(newVal: HandoverDevice[]) {
      if (this.isInternalDeviceSync) {
        return
      }
      this.$emit('devices-without-rooms', newVal, this.object || null, 'device')
    },
    devices(newVal: HandoverDevice[], oldVal: HandoverDevice[]) {
      if (JSON.stringify(newVal) === JSON.stringify(oldVal)) {
        return
      }
      this.runCheckDevices()
    },
    handoverDeletedDeviceIds(newVal: string[], oldVal: string[]) {
      if (newVal.length === 0 && oldVal.length > 0) {
        this.devicesStepDeletedDeviceIds = []
      }
      this.$nextTick(() => {
        this.runCheckDevices()
      })
    },
    resetDevicesWithoutRooms(newVal: boolean) {
      if (newVal) {
        this.devicesWithoutRooms = []
        this.runCheckDevices()
        this.$emit('reset-complete')
      }
    },
    preSelectedRooms: {
      handler(newVal: HandoverRoom[], oldVal: HandoverRoom[]) {
        if (this.source === 'template') {
          const newRoomIds = this.getRoomIdsSignature(newVal)
          const selectedRoomIds = this.getRoomIdsSignature(this.selectedRooms)
          if (newRoomIds === selectedRoomIds) {
            return
          }
        } else if (JSON.stringify(newVal) === JSON.stringify(this.selectedRooms) || JSON.stringify(newVal) === JSON.stringify(oldVal)) {
          return
        }
        this.syncSelectedRoomsFromPreSelected(newVal)
        this.runCheckDevices()
      },
      deep: true
    },
    suggestionDevices(newVal: HandoverDevice[], oldVal: HandoverDevice[]) {
      if (JSON.stringify(newVal) !== JSON.stringify(oldVal)) {
        this.loadAvailableRooms()
      }
    },
    '$i18n.locale'() {
      if (this.suggestionDevices.length === 0) {
        this.loadAvailableRooms()
      }
    }
  },
  mounted() {
    if (this.source === 'template') {
      const preSelectedIds = this.getRoomIdsSignature(this.preSelectedRooms)
      const selectedIds = this.getRoomIdsSignature(this.selectedRooms)
      if (preSelectedIds !== selectedIds) {
        this.syncSelectedRoomsFromPreSelected(this.preSelectedRooms)
      }
    } else if (JSON.stringify(this.preSelectedRooms) !== JSON.stringify(this.selectedRooms)) {
      this.syncSelectedRoomsFromPreSelected(this.preSelectedRooms)
    }
    this.runCheckDevices()
    this.loadAvailableRooms()
  },
  methods: {
    ...mapActions('employee', ['loadEmployees']),
    ...mapGetters('employee', ['getEmployees']),
    getRoomIdsSignature(rooms: HandoverRoom[]): string {
      return rooms.map(room => room.id).sort().join(',')
    },
    syncSelectedRoomsFromPreSelected(preSelectedRooms: HandoverRoom[]) {
      this.selectedRooms = []
      preSelectedRooms.forEach(room => {
        const roomCopy = { ...room, devices: [] }
        const selectedRoom = this.availableRooms.find(r => r.id === room.id)
        if (selectedRoom) {
          this.selectedRooms.push(selectedRoom as HandoverRoom)
          this.availableRooms = this.availableRooms.filter(r => r.id !== room.id)
        } else {
          this.selectedRooms.push(roomCopy)
        }
      })
      this.selectedRooms = this.selectedRooms.filter((room, index, self) =>
        index === self.findIndex((t) => t.id === room.id)
      )
    },
    runCheckDevices() {
      this.isInternalDeviceSync = true
      this.checkDevices()
      this.$nextTick(() => {
        this.isInternalDeviceSync = false
      })
    },
    openAddDeviceModal(room: HandoverRoom, event: Event) {
      event.stopPropagation()
      const modalRoom = JSON.parse(JSON.stringify({
        ...room,
        devices: room.devices.filter(d => !this.isDeviceUninstalledInPast(d))
      }))
      this.selectedRoomModal = modalRoom
      this.addDeviceToEditInit = JSON.parse(JSON.stringify(modalRoom))
      let exactMatch = this.mappingKeys.find(k => k.word.toLowerCase() === room.name.toLowerCase())
      if (exactMatch) {
        this.roomAvailable = this.availableRooms.find(w => w.value === exactMatch.word || w.name === exactMatch.mapping)
      } else {
        this.roomAvailable = this.availableRooms.find(w => w.value.toLowerCase() === room.name.toLowerCase() || w.name.toLowerCase() === room.name.toLowerCase())
      }
      this.$nextTick(() => {
        this.$refs.addDevice.show()
      })
    },
    hideAddDeviceModal() {
      this.$refs.addDevice.hide()
      this.selectedRoomModal = null
      this.roomAvailable = null
    },
    checkDevices() {
      this.devices.forEach(device => {
        const room = this.selectedRooms.find(r => r.id === device.roomId)
        device.value = device.type === '' || device.type === null || device.type === 'DEVICE_TYPE_UNDEFINED' ? '-' : this.$t('message.onBoarding.devices.typeValues.' + device.type)

        const isDeviceDeleted = this.devicesStepDeletedDeviceIds.includes(device.id) ||
          this.devicesStepDeletedDeviceIds.includes(device.internalId) ||
          this.handoverDeletedDeviceIds.includes(device.id) ||
          this.handoverDeletedDeviceIds.includes(device.internalId)

        if (room && !room.devices.find(d => (d.internalId && d.internalId === device.internalId) || d.id === device.id)) {
          if (!isDeviceDeleted) {
            room.devices.push(device)
          }
          room.devices.sort(this.compareDeviceOrder)
        } else {
          if (
            (device.roomId === '' || !this.selectedRooms.some(r => r.id === device.roomId)) &&
            (!this.devicesWithoutRooms.find(d => d.id === device.id)) &&
            device.type !== 'DEVICE_TYPE_LOCKING_KEY' &&
            device.type !== 'DEVICE_TYPE_LOCKING_WIRELESS_TRANSMITTER'
          ) {
            if (device.roomId === '' && !isDeviceDeleted) {
              // Replace any unsaved device of the same type and referenceId with the saved version
              const unsavedDeviceIndex = this.devicesWithoutRooms.findIndex(d =>
                d.internalId && !d.id && d.type === device.type && d.referenceId === device.referenceId
              )
              if (unsavedDeviceIndex > -1) {
                this.$set(this.devicesWithoutRooms, unsavedDeviceIndex, device)
              } else {
                this.devicesWithoutRooms.push(device)
              }
            }
          }
        }
      })
      this.devicesWithoutRooms.sort(this.compareDeviceOrder)
    },
    compareDeviceOrder(a: HandoverDevice, b: HandoverDevice): number {
      const aMissing = a.order === undefined || a.order === null || a.order < 1
      const bMissing = b.order === undefined || b.order === null || b.order < 1
      if (aMissing && bMissing) return 0
      if (aMissing) return 1
      if (bMissing) return -1
      return a.order - b.order
    },
    assignSequentialDeviceOrder(devices: HandoverDevice[]) {
      devices.forEach((device, index) => {
        device.order = index + 1
        device.edited = true
      })
    },
    mergeVisibleRoomDevices(roomId: string, visibleDevices: HandoverDevice[]) {
      const roomIndex = this.selectedRooms.findIndex(r => r.id === roomId)
      if (roomIndex < 0) {
        return
      }
      const hidden = this.selectedRooms[roomIndex].devices.filter(d => this.isDeviceUninstalledInPast(d))
      this.selectedRooms[roomIndex].devices = [...visibleDevices, ...hidden]
    },
    addSuggestedLetterbox() {
      const newLetterbox = JSON.parse(JSON.stringify(helper.emptyDevice))
      newLetterbox.internalId = Date.now() + '_DEVICE_TYPE_OTHER_INTERIOR2_LETTER_BOX'
      newLetterbox.ownerId = this.object.ownerId
      newLetterbox.referenceId = this.object.id
      newLetterbox.type = 'DEVICE_TYPE_OTHER_INTERIOR2_LETTER_BOX'
      newLetterbox.value = this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_LETTER_BOX')
      newLetterbox.lockingKey.description = this.$t('message.processHandover.devices.letterbox')
      this.devicesWithoutRooms.push(newLetterbox)
    },
    // Strip ids so handover device mutations don't hit the original template device.
    cloneAsNewDevice(device: HandoverDevice): HandoverDevice {
      const newDevice = JSON.parse(JSON.stringify(device))
      delete newDevice.id
      delete newDevice.numericId
      delete newDevice.edited
      return newDevice
    },
    isSameDevice(a: HandoverDevice, b: HandoverDevice): boolean {
      if (a.internalId && b.internalId) {
        return a.internalId === b.internalId
      }
      return !!a.id && !!b.id && a.id === b.id
    },
    addSuggestedTemplateDevice(device: HandoverDevice) {
      const newDevice = this.cloneAsNewDevice(device)
      newDevice.internalId = `temp_device_${Date.now()}_${device.type}`
      newDevice.ownerId = this.object ? this.object.ownerId : this.accountId
      newDevice.referenceId = this.object?.id || 'template'
      newDevice.roomId = ''
      newDevice.value = this.$t('message.onBoarding.devices.typeValues.' + device.type)
      this.devicesWithoutRooms.push(newDevice)
    },
    editDevice(device: HandoverDevice, room: HandoverRoom | null) {
      if (room) {
        device.roomId = room.id
      }
      this.deviceToEdit = JSON.parse(JSON.stringify(device))
      this.deviceToEditInit = JSON.parse(JSON.stringify(device))
      this.internalKey++
      this.selectedEditedDevice = JSON.parse(JSON.stringify(device))
      this.$nextTick(() => {
        this.$refs.editDevice.show()
      })
    },
    hideEditDeviceModal() {
      this.$refs.editDevice.hide()
      this.deviceToEdit = null
      this.selectedEditedDevice = null
      this.pendingNewDevice = null
    },
    deviceUpdated(device: HandoverDevice) {
      device.edited = true
      this.selectedEditedDevice = JSON.parse(JSON.stringify(device))
    },
    saveDevice() {
      if (this.$refs['object-device'] && this.$refs['object-device'].isInvalid()) {
        return
      }
      if (!this.selectedEditedDevice) {
        this.hideEditDeviceModal()
        return
      }

      let deviceToUpdate: HandoverDevice | null = null

      if (this.isDeviceUninstalledInPast(this.selectedEditedDevice)) {
        Vue.toasted.show(this.$t('message.processHandover.devices.cannotAddUninstalledDevice'), { type: 'error' })
        return
      }

      if (this.selectedEditedDevice.roomId) {
        if (this.selectedRoomModal) {
          const deviceIndex = this.selectedRoomModal.devices.findIndex(d => d.internalId && this.selectedEditedDevice.internalId ? d.internalId === this.selectedEditedDevice.internalId : d.id === this.selectedEditedDevice.id)
          if (deviceIndex > -1) {
            this.$set(this.selectedRoomModal.devices, deviceIndex, JSON.parse(JSON.stringify(this.selectedEditedDevice)))
            deviceToUpdate = this.selectedRoomModal.devices[deviceIndex]
          }
          this.$nextTick(() => {
            this.selectedRoomModal.devices[deviceIndex].value = this.$t('message.onBoarding.devices.typeValues.' + this.selectedRoomModal.devices[deviceIndex].type)
          })
          const newRoomId = this.selectedEditedDevice.roomId
          const oldRoomId = this.deviceToEditInit.roomId
          if (oldRoomId !== newRoomId) {
            // Remove device from the old room
            const oldRoomIndex = this.selectedRooms.findIndex(r => r.id === oldRoomId)
            if (oldRoomIndex > -1) {
              this.selectedRooms[oldRoomIndex].devices = this.selectedRooms[oldRoomIndex].devices.filter(d => !this.isSameDevice(d, this.selectedEditedDevice))
              this.selectedRoomModal.devices = this.selectedRoomModal.devices.filter(d => !this.isSameDevice(d, this.selectedEditedDevice))
            }

            const newRoomIndex = this.selectedRooms.findIndex(r => r.id === newRoomId)
            if (newRoomIndex > -1) {
              this.selectedRooms[newRoomIndex].devices.push(this.selectedEditedDevice)
              deviceToUpdate = this.selectedRooms[newRoomIndex].devices[this.selectedRooms[newRoomIndex].devices.length - 1]
            } else {
              const newRoom = this.availableRooms.find(r => r.id === newRoomId)
              if (newRoom) {
                newRoom.devices = [this.selectedEditedDevice]
                this.selectedRooms.push(newRoom)
                deviceToUpdate = this.selectedRooms[this.selectedRooms.length - 1].devices[0]
              }
            }
          }
        } else {
          const roomIndex = this.selectedRooms.findIndex(r => r.devices.some(x => x.internalId && this.selectedEditedDevice.internalId ? x.internalId === this.selectedEditedDevice.internalId : x.id === this.selectedEditedDevice.id))
          if (roomIndex > -1) {
            const deviceIndex = this.selectedRooms[roomIndex].devices.findIndex(d => d.internalId && this.selectedEditedDevice.internalId ? d.internalId === this.selectedEditedDevice.internalId : d.id === this.selectedEditedDevice.id)
            if (deviceIndex > -1) {
              this.$set(this.selectedRooms[roomIndex].devices, deviceIndex, JSON.parse(JSON.stringify(this.selectedEditedDevice)))
              deviceToUpdate = this.selectedRooms[roomIndex].devices[deviceIndex]
              this.$nextTick(() => {
                this.selectedRooms[roomIndex].devices[deviceIndex].value = this.$t('message.onBoarding.devices.typeValues.' + this.selectedRooms[roomIndex].devices[deviceIndex].type)
              })
            }
          }
          const newRoomId = this.selectedEditedDevice.roomId
          const oldRoomId = this.deviceToEditInit.roomId
          if (oldRoomId !== newRoomId) {
            // Remove device from the old room
            const oldRoomIndex = this.selectedRooms.findIndex(r => r.id === oldRoomId)
            if (oldRoomIndex > -1) {
              this.selectedRooms[oldRoomIndex].devices = this.selectedRooms[oldRoomIndex].devices.filter(d => !this.isSameDevice(d, this.selectedEditedDevice))
            }

            // Add device to the new room
            const newRoomIndex = this.selectedRooms.findIndex(r => r.id === newRoomId)
            if (newRoomIndex > -1) {
              this.selectedRooms[newRoomIndex].devices.push(this.selectedEditedDevice)
              deviceToUpdate = this.selectedRooms[newRoomIndex].devices[this.selectedRooms[newRoomIndex].devices.length - 1]
            } else {
              // If the new room is not in the selectedRooms, add it
              const newRoom = this.availableRooms.find(r => r.id === newRoomId)
              if (newRoom) {
                newRoom.devices = [this.selectedEditedDevice]
                this.selectedRooms.push(newRoom)
                deviceToUpdate = this.selectedRooms[this.selectedRooms.length - 1].devices[0]
              }
            }
            if (oldRoomId === '' && newRoomId !== '') {
              this.devicesWithoutRooms = this.devicesWithoutRooms.filter(d => !this.isSameDevice(d, this.selectedEditedDevice))
              this.$emit('devices-without-rooms', this.devicesWithoutRooms, this.object, 'device')
            }
          }
        }
        this.$emit('selected-rooms', this.selectedRooms, this.object || null, 'device')
      } else {
        const newRoomId = this.selectedEditedDevice.roomId
        const oldRoomId = this.deviceToEditInit.roomId
        const deviceIndex = this.devicesWithoutRooms.findIndex(d => d.internalId && this.selectedEditedDevice.internalId ? d.internalId === this.selectedEditedDevice.internalId : d.id === this.selectedEditedDevice.id)
        if (deviceIndex > -1) {
          this.$set(this.devicesWithoutRooms, deviceIndex, JSON.parse(JSON.stringify(this.selectedEditedDevice)))
          deviceToUpdate = this.devicesWithoutRooms[deviceIndex]
          this.$emit('devices-without-rooms', this.devicesWithoutRooms, this.object || null, 'device')
          this.$nextTick(() => {
            this.devicesWithoutRooms[deviceIndex].value = this.$t('message.onBoarding.devices.typeValues.' + this.devicesWithoutRooms[deviceIndex].type)
          })
        } else if (oldRoomId !== '' && newRoomId === '') {
          const oldRoomIndex = this.selectedRooms.findIndex(r => r.id === oldRoomId)
          if (oldRoomIndex > -1) {
            this.selectedRooms[oldRoomIndex].devices = this.selectedRooms[oldRoomIndex].devices.filter(d => !this.isSameDevice(d, this.selectedEditedDevice))
          }
          this.$emit('selected-rooms', this.selectedRooms, this.object, 'device')
          this.devicesWithoutRooms.push(this.selectedEditedDevice)
          deviceToUpdate = this.devicesWithoutRooms[this.devicesWithoutRooms.length - 1]
          this.$emit('devices-without-rooms', this.devicesWithoutRooms, this.object, 'device')
        }

        if (this.pendingNewDevice && this.selectedEditedDevice) {
          this.selectedEditedDevice.value = this.$t('message.onBoarding.devices.typeValues.' + this.selectedEditedDevice.type)
          this.devicesWithoutRooms.push(this.selectedEditedDevice)
          this.$emit('devices-without-rooms', this.devicesWithoutRooms, this.object || null, 'device')
          this.pendingNewDevice = null
        }
      }

      this.$refs.editDevice.hide()

      if (deviceToUpdate) {
        deviceToUpdate.value = this.$t('message.onBoarding.devices.typeValues.' + deviceToUpdate.type)
      }
      this.selectedEditedDevice = null
      this.deviceToEdit = null
    },
    addDevice(event: Event) {
      event.stopPropagation()
      const newDevice: HandoverDevice = JSON.parse(JSON.stringify(helper.emptyDevice))
      newDevice.internalId = Date.now()
      newDevice.ownerId = this.object ? this.object.ownerId : this.accountId
      newDevice.referenceId = this.object?.id || 'template'
      newDevice.value = this.$t('message.onBoarding.devices.newDevice')
      if (this.selectedRoomModal) {
        this.selectedRoomModal.devices.push(newDevice)
      } else {
        this.pendingNewDevice = newDevice
        this.editDevice(newDevice, null)
      }
      this.clearSelection()
    },
    deleteDevice() {
      this.$emit('add-init-key')
      this.devicesStepDeletedDeviceIds.push(this.selectedDevice.id || this.selectedDevice.internalId)
      const selectedRoom = this.selectedRooms.find(r => (r.id && r.id === this.selectedRoom?.id) || (r.internalId && r.internalId === this.selectedRoom?.internalId))
      if (selectedRoom) {
        selectedRoom.devices = selectedRoom.devices.filter(d =>
          !((d.internalId && this.selectedDevice.internalId && d.internalId === this.selectedDevice.internalId) || (d.id && this.selectedDevice.id && d.id === this.selectedDevice.id))
        )
      } else {
        this.devicesWithoutRooms = this.devicesWithoutRooms.filter(d =>
          !((d.internalId && this.selectedDevice.internalId && d.internalId === this.selectedDevice.internalId) || (d.id && this.selectedDevice.id && d.id === this.selectedDevice.id))
        )
      }
      this.selectedRoom = null
      this.$bvModal.hide('confirmationModalDelete_' + this.objectId)
      this.clearSelection()
    },
    deleteDeviceModal() {
      this.devicesStepDeletedDeviceIds.push(this.selectedDevice.id || this.selectedDevice.internalId)

      this.selectedRoomModal.devices = this.selectedRoomModal.devices.filter(d => (d.internalId && this.selectedDevice.internalId && d.internalId !== this.selectedDevice.internalId) || (d.id !== this.selectedDevice.id))
      this.$bvModal.hide('confirmationModalDelete_' + this.objectId)
      this.clearSelection()
    },
    saveEditDeviceOfRoom() {
      if (this.selectedRoomModal) {
        const visibleDevices = JSON.parse(JSON.stringify(this.selectedRoomModal.devices))
          .filter((device: HandoverDevice) => !this.isDeviceUninstalledInPast(device))
        this.assignSequentialDeviceOrder(visibleDevices)
        this.mergeVisibleRoomDevices(this.selectedRoomModal.id, visibleDevices)
      }
      this.$emit('selected-rooms', this.selectedRooms, this.object || null, 'device')
      this.$refs.addDevice.hide()
      this.selectedRoomModal = null
      this.clearSelection()
    },
    clearSelection() {
      this.selectedDevice = null
    },
    toggleCollapse(index: number) {
      this.$set(this.collapsedRooms, index, !this.collapsedRooms[index])
    },
    loadAvailableRooms() {
      this.loading = true
      this.availableRooms = []
      if (this.suggestionDevices.length > 0) {
        this.suggestionRooms.forEach(room => {
          const selectedDevices = this.suggestionDevices.filter(d => (d.roomId === room.id))
          this.availableRooms.push({
            name: room.name,
            value: room.name,
            devices: selectedDevices?.map(({ id, type, ...rest }) => ({ id, type, ...rest, internalId: id, value: this.$t('message.onBoarding.devices.typeValues.' + type) })) || []
          })
        })
      } else {
        const parkingRoom = {
          name: 'parking',
          value: this.$t('message.processHandover.rooms.parking'),
          devices: [
            { internalId: '1_parking', type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
            { internalId: '2_parking', type: 'DEVICE_TYPE_GARDENING_TRANSITION_POSITION_GROUND_MARKING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_GARDENING_TRANSITION_POSITION_GROUND_MARKING') },
            { internalId: '3_parking', type: 'DEVICE_TYPE_WINDOW_DOOR_GARAGE_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_DOOR_GARAGE_DOOR') },
            { internalId: '4_parking', type: 'DEVICE_TYPE_CARPENTER_CLOSET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_CLOSET') },
            { internalId: '5_parking', type: 'DEVICE_TYPE_SPECIAL_SANITARY_WATER_TAP', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SPECIAL_SANITARY_WATER_TAP') }
          ]
        }
        const secondaryRoom = {
          name: 'secondaryRoom',
          value: this.$t('message.processHandover.rooms.secondaryRoom'),
          devices: [
            { internalId: '1_secondaryRoom', type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
            { internalId: '2_secondaryRoom', type: 'DEVICE_TYPE_FLOORING_SKIRTING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_SKIRTING') },
            { internalId: '3_secondaryRoom', type: 'DEVICE_TYPE_WALL_COVERING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WALL_COVERING') },
            { internalId: '4_secondaryRoom', type: 'DEVICE_TYPE_CEILING_REGULAR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CEILING_REGULAR') },
            { internalId: '5_secondaryRoom', type: 'DEVICE_TYPE_CARPENTER_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_DOOR') },
            { internalId: '6_secondaryRoom', type: 'DEVICE_TYPE_LOCKING_KEY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOCKING_KEY') },
            { internalId: '7_secondaryRoom', type: 'DEVICE_TYPE_WINDOW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW') },
            { internalId: '8_secondaryRoom', type: 'DEVICE_TYPE_WINDOW_WINDOW_FRAME', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_FRAME') },
            { internalId: '9_secondaryRoom', type: 'DEVICE_TYPE_WINDOW_WINDOW_SILL', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_SILL') },
            { internalId: '10_secondaryRoom', type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH') },
            { internalId: '11_secondaryRoom', type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
            { internalId: '12_secondaryRoom', type: 'DEVICE_TYPE_LIGHTS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS') }
          ]
        }

        if (this.object === null || this.object.category === 'APARTMENT' || this.object.category === 'HOUSE' || this.object.category === 'COMMERCIAL' || this.object.category === 'GASTRONOMY') {
          this.availableRooms.push(
            {
              name: 'kitchen',
              value: this.$t('message.processHandover.rooms.kitchen'),
              devices: [
                { internalId: 1, type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
                { internalId: 2, type: 'DEVICE_TYPE_FLOORING_SKIRTING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_SKIRTING') },
                { internalId: 3, type: 'DEVICE_TYPE_WALL_COVERING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WALL_COVERING') },
                { internalId: 4, type: 'DEVICE_TYPE_CEILING_REGULAR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CEILING_REGULAR') },
                { internalId: 5, type: 'DEVICE_TYPE_CARPENTER_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_DOOR') },
                { internalId: 6, type: 'DEVICE_TYPE_LOCKING_KEY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOCKING_KEY') },
                { internalId: 7, type: 'DEVICE_TYPE_WINDOW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW') },
                { internalId: 8, type: 'DEVICE_TYPE_WINDOW_WINDOW_FRAME', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_FRAME') },
                { internalId: 9, type: 'DEVICE_TYPE_WINDOW_WINDOW_SILL', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_SILL') },
                { internalId: 10, type: 'DEVICE_TYPE_OUTER_FINISHES', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OUTER_FINISHES') },
                { internalId: 11, type: 'DEVICE_TYPE_OTHER_INTERIOR2_CRANK', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_CRANK') },
                { internalId: 12, type: 'DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT') },
                { internalId: 13, type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH') },
                { internalId: 14, type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
                { internalId: 15, type: 'DEVICE_TYPE_LIGHTS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS') },
                { internalId: 16, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_TOP_CABINET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_TOP_CABINET') },
                { internalId: 17, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_UNDER_CABINET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_UNDER_CABINET') },
                { internalId: 18, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_DRAWER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_DRAWER') },
                { internalId: 19, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_WASTE', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_WASTE') },
                { internalId: 20, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_KITCHEN_COVER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_KITCHEN_COVER') },
                { internalId: 21, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_FLUSHING_BASIN', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_FLUSHING_BASIN') },
                { internalId: 22, type: 'DEVICE_TYPE_OTHER_INTERIOR2_ARMATURE', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_ARMATURE') },
                { internalId: 23, type: 'DEVICE_TYPE_OTHER_INTERIOR2_OPERATING_GARNITURE', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_OPERATING_GARNITURE') },
                { internalId: 24, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_HOB', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_HOB') },
                { internalId: 25, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_STEAM_EXHAUST_HOOD', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_STEAM_EXHAUST_HOOD') },
                { internalId: 26, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_FILTER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_FILTER') },
                { internalId: 27, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_OVEN', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_OVEN') },
                { internalId: 28, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_OVEN_ACCESSORIES', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_OVEN_ACCESSORIES') },
                { internalId: 29, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_STEAMER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_STEAMER') },
                { internalId: 30, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_STEAMER_ACCESSORIES', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_STEAMER_ACCESSORIES') },
                { internalId: 31, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_REFRIGERATOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_REFRIGERATOR') },
                { internalId: 32, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_REFRIGERATOR_ACCESSORIES', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_REFRIGERATOR_ACCESSORIES') },
                { internalId: 33, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_FREEZER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_FREEZER') },
                { internalId: 34, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_DISH_WASHER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_DISH_WASHER') },
                { internalId: 35, type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_DISH_WASHER_CUTLERY_TRAY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_DISH_WASHER_CUTLERY_TRAY') }]
            },
            {
              name: 'wetRoom',
              value: this.$t('message.processHandover.rooms.wetRoom'),
              devices: [
                { internalId: 1, type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
                { internalId: 2, type: 'DEVICE_TYPE_FLOORING_SKIRTING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_SKIRTING') },
                { internalId: 3, type: 'DEVICE_TYPE_WALL_COVERING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WALL_COVERING') },
                { internalId: 4, type: 'DEVICE_TYPE_CEILING_REGULAR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CEILING_REGULAR') },
                { internalId: 5, type: 'DEVICE_TYPE_CARPENTER_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_DOOR') },
                { internalId: 6, type: 'DEVICE_TYPE_LOCKING_KEY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOCKING_KEY') },
                { internalId: 7, type: 'DEVICE_TYPE_WINDOW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW') },
                { internalId: 8, type: 'DEVICE_TYPE_WINDOW_WINDOW_FRAME', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_FRAME') },
                { internalId: 9, type: 'DEVICE_TYPE_WINDOW_WINDOW_SILL', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_SILL') },
                { internalId: 10, type: 'DEVICE_TYPE_OUTER_FINISHES', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OUTER_FINISHES') },
                { internalId: 11, type: 'DEVICE_TYPE_OTHER_INTERIOR2_CRANK', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_CRANK') },
                { internalId: 12, type: 'DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT') },
                { internalId: 13, type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH') },
                { internalId: 14, type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
                { internalId: 15, type: 'DEVICE_TYPE_LIGHTS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS') },
                { internalId: 16, type: 'DEVICE_TYPE_SANITARY_EQUIPMENT_BATHROOM_FURNITURE', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_EQUIPMENT_BATHROOM_FURNITURE') },
                { internalId: 17, type: 'DEVICE_TYPE_SANITARY_EQUIPMENT_MIRROR_CABINETS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_EQUIPMENT_MIRROR_CABINETS') },
                { internalId: 18, type: 'DEVICE_TYPE_SANITARY_WASHBASIN', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_WASHBASIN') },
                { internalId: 19, type: 'DEVICE_TYPE_OTHER_INTERIOR2_ARMATURE', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_ARMATURE') },
                { internalId: 20, type: 'DEVICE_TYPE_OTHER_INTERIOR2_SETS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_SETS') },
                { internalId: 21, type: 'DEVICE_TYPE_OTHER_INTERIOR2_OPERATING_GARNITURE', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_OPERATING_GARNITURE') },
                { internalId: 22, type: 'DEVICE_TYPE_OTHER_INTERIOR2_SETS_BATH_TOWEL_HOLDER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_SETS_BATH_TOWEL_HOLDER') },
                { internalId: 23, type: 'DEVICE_TYPE_SANITARY_TOILET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_TOILET') },
                { internalId: 24, type: 'DEVICE_TYPE_SANITARY_TOILET_COVER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_TOILET_COVER') },
                { internalId: 25, type: 'DEVICE_TYPE_SANITARY_FLUSH_TANK', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_FLUSH_TANK') },
                { internalId: 26, type: 'DEVICE_TYPE_OTHER_INTERIOR2_SETS_PAPER_HOLDER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_SETS_PAPER_HOLDER') },
                { internalId: 27, type: 'DEVICE_TYPE_VENTILATION_EXHAUST_AIR_FAN', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_VENTILATION_EXHAUST_AIR_FAN') },
                { internalId: 28, type: 'DEVICE_TYPE_SANITARY_BATHTUB', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_BATHTUB') },
                { internalId: 29, type: 'DEVICE_TYPE_SANITARY_SHOWER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_SHOWER') },
                { internalId: 30, type: 'DEVICE_TYPE_SANITARY_SHOWER_HEAD_HOSE', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_SHOWER_HEAD_HOSE') },
                { internalId: 31, type: 'DEVICE_TYPE_SANITARY_SHOWER_ROD', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_SHOWER_ROD') },
                { internalId: 32, type: 'DEVICE_TYPE_SANITARY_SHOWER_CABIN', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SANITARY_SHOWER_CABIN') }
              ]
            },
            {
              name: 'room',
              value: this.$t('message.processHandover.rooms.room'),
              devices: [
                { internalId: '1room', type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
                { internalId: '2room', type: 'DEVICE_TYPE_FLOORING_SKIRTING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_SKIRTING') },
                { internalId: '3room', type: 'DEVICE_TYPE_WALL_COVERING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WALL_COVERING') },
                { internalId: '4room', type: 'DEVICE_TYPE_CEILING_REGULAR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CEILING_REGULAR') },
                { internalId: '5room', type: 'DEVICE_TYPE_CARPENTER_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_DOOR') },
                { internalId: '6room', type: 'DEVICE_TYPE_LOCKING_KEY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOCKING_KEY') },
                { internalId: '7room', type: 'DEVICE_TYPE_WINDOW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW') },
                { internalId: '8room', type: 'DEVICE_TYPE_WINDOW_WINDOW_FRAME', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_FRAME') },
                { internalId: '9room', type: 'DEVICE_TYPE_WINDOW_WINDOW_SILL', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_SILL') },
                { internalId: '10room', type: 'DEVICE_TYPE_OUTER_FINISHES', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OUTER_FINISHES') },
                { internalId: '11room', type: 'DEVICE_TYPE_OTHER_INTERIOR2_CRANK', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_CRANK') },
                { internalId: '12room', type: 'DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT') },
                { internalId: '13room', type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH') },
                { internalId: '14room', type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
                { internalId: '15room', type: 'DEVICE_TYPE_LIGHTS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS') },
                { internalId: '16room', type: 'DEVICE_TYPE_CARPENTER_BUILT_IN_CLOSETS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_BUILT_IN_CLOSETS') },
                { internalId: '17room', type: 'DEVICE_TYPE_CARPENTER_CLOSET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_CLOSET') },
                { internalId: '18room', type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_MULTIMEDIA', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_MULTIMEDIA') },
                { internalId: '19room', type: 'DEVICE_TYPE_INSULATION_CHIMNEY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_INSULATION_CHIMNEY') },
                { internalId: '20room', type: 'DEVICE_TYPE_SUPERSTRUCTURE_RAILING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SUPERSTRUCTURE_RAILING') }
              ]
            },
            {
              name: 'corridor_entrance',
              value: this.$t('message.processHandover.rooms.corridor_entrance'),
              devices: [
                { internalId: '1_corridor_entrance', type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
                { internalId: '2_corridor_entrance', type: 'DEVICE_TYPE_FLOORING_SKIRTING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_SKIRTING') },
                { internalId: '3_corridor_entrance', type: 'DEVICE_TYPE_WALL_COVERING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WALL_COVERING') },
                { internalId: '4_corridor_entrance', type: 'DEVICE_TYPE_CEILING_REGULAR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CEILING_REGULAR') },
                { internalId: '5_corridor_entrance', type: 'DEVICE_TYPE_CARPENTER_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_DOOR') },
                { internalId: '6_corridor_entrance', type: 'DEVICE_TYPE_LOCKING_KEY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOCKING_KEY') },
                { internalId: '7_corridor_entrance', type: 'DEVICE_TYPE_WINDOW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW') },
                { internalId: '8_corridor_entrance', type: 'DEVICE_TYPE_WINDOW_WINDOW_FRAME', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_FRAME') },
                { internalId: '9_corridor_entrance', type: 'DEVICE_TYPE_WINDOW_WINDOW_SILL', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_SILL') },
                { internalId: '10_corridor_entrance', type: 'DEVICE_TYPE_OUTER_FINISHES', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OUTER_FINISHES') },
                { internalId: '11_corridor_entrance', type: 'DEVICE_TYPE_OTHER_INTERIOR2_CRANK', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_CRANK') },
                { internalId: '12_corridor_entrance', type: 'DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT') },
                { internalId: '13_corridor_entrance', type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH') },
                { internalId: '14_corridor_entrance', type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
                { internalId: '15_corridor_entrance', type: 'DEVICE_TYPE_LIGHTS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS') },
                { internalId: '16_corridor_entrance', type: 'DEVICE_TYPE_CARPENTER_BUILT_IN_CLOSETS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_BUILT_IN_CLOSETS') },
                { internalId: '17_corridor_entrance', type: 'DEVICE_TYPE_CARPENTER_CLOSET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_CLOSET') },
                { internalId: '18_corridor_entrance', type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_INTERPHONE', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_INTERPHONE') }
              ]
            },
            {
              name: 'reduit_storage',
              value: this.$t('message.processHandover.rooms.reduit_storage'),
              devices: [
                { internalId: '1_reduit_storage', type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
                { internalId: '2_reduit_storage', type: 'DEVICE_TYPE_FLOORING_SKIRTING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_SKIRTING') },
                { internalId: '3_reduit_storage', type: 'DEVICE_TYPE_WALL_COVERING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WALL_COVERING') },
                { internalId: '4_reduit_storage', type: 'DEVICE_TYPE_CEILING_REGULAR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CEILING_REGULAR') },
                { internalId: '5_reduit_storage', type: 'DEVICE_TYPE_CARPENTER_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_DOOR') },
                { internalId: '6_reduit_storage', type: 'DEVICE_TYPE_LOCKING_KEY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOCKING_KEY') },
                { internalId: '7_reduit_storage', type: 'DEVICE_TYPE_WINDOW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW') },
                { internalId: '8_reduit_storage', type: 'DEVICE_TYPE_WINDOW_WINDOW_FRAME', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_FRAME') },
                { internalId: '9_reduit_storage', type: 'DEVICE_TYPE_WINDOW_WINDOW_SILL', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_SILL') },
                { internalId: '10_reduit_storage', type: 'DEVICE_TYPE_OUTER_FINISHES', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OUTER_FINISHES') },
                { internalId: '11_reduit_storage', type: 'DEVICE_TYPE_OTHER_INTERIOR2_CRANK', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_CRANK') },
                { internalId: '12_reduit_storage', type: 'DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_HEAT_GENERATION_HEATING_ELEMENT') },
                { internalId: '13_reduit_storage', type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH') },
                { internalId: '14_reduit_storage', type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
                { internalId: '15_reduit_storage', type: 'DEVICE_TYPE_LIGHTS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS') }
              ]
            },
            {
              name: 'balcony_seatingArea',
              value: this.$t('message.processHandover.rooms.balcony_seatingArea'),
              devices: [
                { internalId: '1_balcony_seatingArea', type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
                { internalId: '13_balcony_seatingArea', type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH') },
                { internalId: '14_balcony_seatingArea', type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
                { internalId: '15_balcony_seatingArea', type: 'DEVICE_TYPE_LIGHTS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS') },
                { internalId: '19_balcony_seatingArea', type: 'DEVICE_TYPE_FACADE_PLASTER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FACADE_PLASTER') },
                { internalId: '20_balcony_seatingArea', type: 'DEVICE_TYPE_OUTER_FINISHES_PRIVACY_SCREEN', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OUTER_FINISHES_PRIVACY_SCREEN') },
                { internalId: '21_balcony_seatingArea', type: 'DEVICE_TYPE_OUTER_FINISHES_SUN_BLINDS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OUTER_FINISHES_SUN_BLINDS') },
                { internalId: '22_balcony_seatingArea', type: 'DEVICE_TYPE_SPECIAL_SANITARY_WATER_TAP', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_SPECIAL_SANITARY_WATER_TAP') }
              ]
            },
            {
              name: 'cellar',
              value: this.$t('message.processHandover.rooms.cellar'),
              devices: [
                { internalId: '1_cellar', type: 'DEVICE_TYPE_FLOORING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING') },
                { internalId: '2_cellar', type: 'DEVICE_TYPE_WALL_COVERING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WALL_COVERING') },
                { internalId: '3_cellar', type: 'DEVICE_TYPE_CARPENTER_CELLAR_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_CELLAR_DOOR') },
                { internalId: '4_cellar', type: 'DEVICE_TYPE_CARPENTER_SHELF', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_SHELF') },
                { internalId: '5_cellar', type: 'DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOW_CURRENT_INSTALLATION_SWITCH') },
                { internalId: '6_cellar', type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
                { internalId: '7_cellar', type: 'DEVICE_TYPE_LIGHTS', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS') },
                { internalId: '8_cellar', type: 'DEVICE_TYPE_WINDOW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW') },
                { internalId: '9_cellar', type: 'DEVICE_TYPE_WINDOW_WINDOW_FRAME', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_FRAME') }
              ]
            },
            parkingRoom,
            secondaryRoom
          )
        }
        if (this.object === null || this.object.category === 'PARKING_SPACE') {
          this.availableRooms.push(parkingRoom)
        }
        if (this.object === null || this.object.category === 'SECONDARY_ROOM') {
          this.availableRooms.push(secondaryRoom)
        }
        // if (this.object === null || this.object.category === 'COMMERCIAL') {
        //  this.availableRooms.push({
        //    name: 'commercial',
        //    value: this.$t('message.processHandover.rooms.commercial'),
        //    devices: [
        //      { internalId: '1_commercial', type: 'DEVICE_TYPE_OTHER_INTERIOR2_SHUTTER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_SHUTTER') },
        //      { internalId: '2_commercial', type: 'DEVICE_TYPE_OTHER_INTERIOR2_BELT', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_INTERIOR2_BELT') },
        //      { internalId: '3_commercial', type: 'DEVICE_TYPE_CARPENTER_DOOR_FRAME', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_DOOR_FRAME') },
        //      { internalId: '4_commercial', type: 'DEVICE_TYPE_CARPENTER_DOOR', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_CARPENTER_DOOR') },
        //      { internalId: '5_commercial', type: 'DEVICE_TYPE_FLOORING_RAW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_RAW') },
        //      { internalId: '6_commercial', type: 'DEVICE_TYPE_LIGHTS_CEILING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LIGHTS_CEILING') },
        //      { internalId: '7_commercial', type: 'DEVICE_TYPE_WINDOW_WINDOW_SILL', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WINDOW_WINDOW_SILL') },
        //      { internalId: '8_commercial', type: 'DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_OTHER_ELECTRICITY_SOCKET') },
        //      { internalId: '9_commercial', type: 'DEVICE_TYPE_FLOORING_SKIRTING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_SKIRTING') },
        //      { internalId: '10_commercial', type: 'DEVICE_TYPE_FLOORING_RAW', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_FLOORING_RAW') },
        //      { internalId: '11_commercial', type: 'DEVICE_TYPE_WALL_COVERING', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_WALL_COVERING') },
        //      { internalId: '12_commercial', type: 'DEVICE_TYPE_LOCKING_KEY', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_LOCKING_KEY') },
        //      { internalId: '13_commercial', type: 'DEVICE_TYPE_KITCHEN_EQUIPMENT_KITCHEN_COVER', value: this.$t('message.onBoarding.devices.typeValues.DEVICE_TYPE_KITCHEN_EQUIPMENT_KITCHEN_COVER') }
        //    ]
        //  })
        // }

        this.addPropertiesToDevices()
      }
      this.loading = false
    },
    addDeviceMethod(device: HandoverDevice) {
      if (!this.selectedRoomModal) return

      if (this.isDeviceUninstalledInPast(device)) {
        Vue.toasted.show(this.$t('message.processHandover.devices.cannotAddUninstalledDevice'), { type: 'error' })
        return
      }

      const newDevice = this.cloneAsNewDevice(device)
      newDevice.internalId = `temp_device_${Date.now()}_${this.selectedRoomModal.devices.length}`
      newDevice.order = this.selectedRoomModal.devices.length + 1
      newDevice.roomId = this.selectedRoomModal.id
      newDevice.referenceId = this.object ? this.object.id : 'template'
      this.selectedRoomModal.devices.push(newDevice)
      this.clearSelection()
    },
    addAllDevices(room: AvailableRoom) {
      if (!this.selectedRoomModal || !room) return

      // Duplicates check for adding confirmation
      const duplicates = room.devices.filter(suggestedDevice =>
        this.selectedRoomModal.devices.some(existingDevice =>
          existingDevice.type === suggestedDevice.type
        )
      )

      const nonDuplicates = room.devices.filter(suggestedDevice =>
        !this.selectedRoomModal.devices.some(existingDevice =>
          existingDevice.type === suggestedDevice.type
        )
      )

      if (duplicates.length > 0) {
        this.duplicateDevices = duplicates
        this.pendingDevices = nonDuplicates
        this.$bvModal.show('duplicateConfirmationModal')
      } else {
        this.addDevicesToRoom(room.devices)
      }
    },
    handleDuplicateConfirmation(includeDuplicates: boolean) {
      if (includeDuplicates) {
        this.addDevicesToRoom([...this.pendingDevices, ...this.duplicateDevices])
      } else {
        this.addDevicesToRoom(this.pendingDevices)
      }

      this.duplicateDevices = []
      this.pendingDevices = []
      this.$bvModal.hide('duplicateConfirmationModal')
    },
    addDevicesToRoom(devices: HandoverDevice[]) {
      const [validDevices, invalidDevices] = devices.reduce(
        ([valid, invalid], device) => {
          this.isDeviceUninstalledInPast(device)
            ? invalid.push(device)
            : valid.push(device)
          return [valid, invalid]
        },
        [[], []]
      )

      if (invalidDevices.length) {
        Vue.toasted.show(this.$t('message.processHandover.devices.someDevicesCannotBeAdded'), { type: 'warning' })
      }

      validDevices.forEach((device, index) => {
        const newDevice = this.cloneAsNewDevice(device)
        newDevice.internalId = `temp_device_${Date.now()}_${index}`
        newDevice.roomId = this.selectedRoomModal.id
        newDevice.order = this.selectedRoomModal.devices.length + 1
        newDevice.referenceId = this.object ? this.object.id : 'template'
        this.selectedRoomModal.devices.push(newDevice)
      })
    },
    showDeleteConfirmation(device: HandoverDevice, e: Event, room: HandoverRoom | null = null) {
      e.preventDefault()
      this.selectedDevice = device
      if (room) {
        this.selectedRoom = room
      }
      this.$bvModal.show('confirmationModalDelete_' + this.objectId)
    },
    addPropertiesToDevices() {
      this.availableRooms.forEach(room => {
        room.devices.forEach(device => {
          this.availableSuggestions.filter(suggestion => suggestion.type === device.type)
          Object.assign(device, {
            internalId: Date.now() + '_' + device.type + '_' + room.name,
            ownerId: this.object ? this.object.ownerId : this.accountId,
            numericId: 99999,
            brand: '',
            referenceId: this.object ? this.object.id : 'template',
            room: room.name,
            installation: null,
            uninstallation: null,
            measureUntil: null,
            measure: 'DEVICE_MEASURE_UNDEFINED',
            type: device.type,
            system: this.availableSuggestions.find(suggestion => suggestion.type === device.type)?.system || 'DEVICE_SYSTEM_UNDEFINED',
            facilityPlant: this.availableSuggestions.find(suggestion => suggestion.type === device.type)?.facilityPlant || 'DEVICE_FACILITY_PLANT_UNDEFINED',
            bkpH: this.availableSuggestions.find(suggestion => suggestion.type === device.type)?.bkp || '',
            lifeSpan: this.availableSuggestions.find(suggestion => suggestion.type === device.type)?.lifeSpan || -1,
            amount: -1,
            value: device.value,
            acquisitionCosts: -1,
            unit: this.availableSuggestions.find(suggestion => suggestion.type === device.type)?.unit || 'DEVICE_UNIT_UNDEFINED',
            cataloguePrice: this.availableSuggestions.find(suggestion => suggestion.type === device.type)?.cataloguePrice || -1,
            maintenance: {
              contact: {
                userReference: '',
                accountId: -1
              },
              costs: {
                currency: 'CHF',
                interval: 'UNDEFINED_INTERVAL'
              },
              lastMaintenance: null,
              nextMaintenance: null,
              subscriptionStart: null,
              subscriptionEnd: null,
              note: ''
            },
            inspection: {
              contact: {
                userReference: '',
                accountId: -1
              },
              costs: {
                currency: 'CHF',
                interval: 'UNDEFINED_INTERVAL'
              },
              lastOfficialControl: null,
              nextOfficialControl: null,
              subscriptionStart: null,
              subscriptionEnd: null,
              note: ''
            },
            revision: {
              contact: {
                userReference: '',
                accountId: -1
              },
              costs: {
                currency: 'CHF',
                interval: 'UNDEFINED_INTERVAL'
              },
              lastOfficialRevision: null,
              nextOfficialRevision: null,
              subscriptionStart: null,
              subscriptionEnd: null,
              note: ''
            },
            condition: 'DEVICE_CONDITION_UNDEFINED',
            note: '',
            documentIds: [],
            images: [],
            isNewItem: true,
            lockingKey: {
              combinedKeys: false,
              combinedKeysNote: '',
              description: '',
              lockingPlanNumber: ''
            }
          })
        })
      })
    },
    selectSelectedRoom(room: HandoverRoom) {
      this.selectedRoom = room
    },
    getContactName(contactId: string): string {
      const contact = this.usedContacts.concat(this.contacts).find(function (contact: Record<string, string>) {
        return contact.id === contactId || contact.objectID === contactId
      })
      return contact ? ', ' + contact.name : '-'
    },
    updateDeviceOrder(room: HandoverRoom) {
      this.assignSequentialDeviceOrder(room.devices)
      this.mergeVisibleRoomDevices(room.id, room.devices)
      this.$emit('selected-rooms', this.selectedRooms, this.object, 'device')
    },
    updateModalDevicesOrder() {
      if (this.selectedRoomModal) {
        this.assignSequentialDeviceOrder(this.selectedRoomModal.devices)
      }
    },
    updateDevicesWithoutRoomsOrder() {
      this.filteredDevicesWithoutRooms.forEach((device, index) => {
        device.order = index + 1
        device.edited = true
      })
    },
    handleDeviceModalClose(event) {
      if (['headerclose', 'esc'].includes(event?.trigger)) {
        const isEdited = this.selectedEditedDevice && JSON.stringify(this.deviceToEdit) !== JSON.stringify(this.deviceToEditInit)
        if (isEdited) {
          this.$refs['device-confirmation-modal'].show()
        }
      }
    },
    saveDeviceAndClose() {
      this.saveDevice()
      this.$refs['device-confirmation-modal'].hide()
    },
    discardDeviceChanges() {
      this.deviceToEdit = JSON.parse(JSON.stringify(this.deviceToEditInit))
      this.selectedEditedDevice = null
      this.pendingNewDevice = null
      this.$refs['device-confirmation-modal'].hide()
      this.$refs.editDevice.hide()
      this.deviceToEdit = null
    },
    cancelDeviceModalClose() {
      this.$refs['device-confirmation-modal'].hide()
      this.$refs.editDevice.show()
    },
    handleAddDeviceModalClose(event) {
      if (['headerclose', 'esc'].includes(event?.trigger)) {
        const hasChanges = this.selectedRoomModal && JSON.stringify(this.selectedRoomModal) !== JSON.stringify(this.addDeviceToEditInit)
        if (hasChanges) {
          this.$refs['add-device-confirmation-modal'].show()
        } else {
          this.hideAddDeviceModal()
        }
      } else {
        this.hideAddDeviceModal()
      }
    },
    saveAddDeviceAndClose() {
      this.saveEditDeviceOfRoom()
      this.$refs['add-device-confirmation-modal'].hide()
    },
    discardAddDeviceChanges() {
      this.selectedRoomModal = JSON.parse(JSON.stringify(this.addDeviceToEditInit))
      this.$refs['add-device-confirmation-modal'].hide()
      this.$refs.addDevice.hide()
      this.selectedRoomModal = null
      this.roomAvailable = null
    },
    cancelAddDeviceModalClose() {
      this.$refs['add-device-confirmation-modal'].hide()
      this.$refs.addDevice.show()
    }
  }
})
</script>

<style lang="scss" scoped>
::v-deep #editDevice .modal-dialog {
  max-width: 80% !important;
}
::v-deep #addDevice .modal-dialog {
  max-width: 80% !important;
}
.room-container {
  margin-bottom: 20px;
}

.room-title {
  padding: 10px;
  background-color: #e9ecef;
  cursor: pointer;
  border: 1px solid #ccc;
  border-radius: 3px;
}
.device-list {
  border-radius: 3px;
  background-color: #fff;
  margin-top: 5px;
}
.arrows {
  display: flex;
  flex-direction: column;
  justify-content: initial;
  margin-top: 10rem;
  align-items: center;
}
.center-add-all {
  margin-top: -5px;
}

.arrows button {
  margin: 5px;
}
.tooltip-message {
  width: 15px;
}

.list-group-item {
  padding: 10px;
  margin: 5px 0;
  border: 1px solid #ddd;
  border-radius: 3px;
  background-color: #f5f5f5;
}
#addDevice .list-group-item.with-add-button {
  &:first-child {
    display: inline-block;
    width: 100%;
    margin: 0;
    margin-top: -13px;
  }
}

.add-device-btn {
  width: 100%;
  padding: 8px;
  margin-top: 10px;
  background-color: #28a745;
  color: white;
  border: none;
  border-radius: 3px;
  cursor: pointer;
}

.add-device-btn:hover {
  background-color: #218838;
}
.list-group-item.selected, .room-title.selected {
  background-color: #d3e2ff;
  border-color: #007bff;
}
.modal-add-all {
  position: relative;
  top: -2.4rem;
}

.condition-dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 50%;
  margin-right: 5px;
}

.condition-dot.DEVICE_CONDITION_PERFECT {
  background-color: #249689;
}

.condition-dot.DEVICE_CONDITION_NORMAL_WEAR {
  background-color: #ffa500;
}

.condition-dot.DEVICE_CONDITION_DEFICIENCY {
  background-color: #ff5963;
}

.duplicate-list {
  margin-top: 1rem;
  padding-left: 1.5rem;
}
</style>
